import { createSelector } from 'reselect';
import { AppState } from '..';
import { ICreativity } from '../creativities/types';
import { isPublishedCreativity } from './validation';

export const selectPlaylists = (state: AppState) => state.playlists.all;

export const selectPlaylistById = (state: AppState, id: string) =>
  (id && state.playlists.all.find(playlist => playlist._id === id)) || null;

export const selectPlaylistsContainingCreativity = (creativityId: string) =>
  createSelector(
    selectPlaylists,
    playlists =>
      creativityId
        ? playlists.filter(playlist =>
            playlist.items.some(item => item.creativity === creativityId)
          )
        : []
  );

export const selectActiveTargetedPlaylistsContainingCreativity = (
  creativityId: string
) =>
  createSelector(selectPlaylistsContainingCreativity(creativityId), playlists =>
    playlists.filter(
      playlist =>
        playlist.active &&
        (playlist.allPlayers ||
          (playlist.players && playlist.players.length) ||
          (playlist.groups && playlist.groups.length))
    )
  );

export const hasPlaylistTargetSelector = (
  state: AppState,
  creativityId: string
) =>
  selectActiveTargetedPlaylistsContainingCreativity(creativityId)(state)
    .length > 0;

export const selectPublishedCreativities = createSelector(
  (state: AppState) => state.creativities.all,
  creativities => creativities.filter(isPublishedCreativity)
);

export const selectPlaylistCreativities = (playlistId: string) =>
  createSelector(
    (state: AppState) => selectPlaylistById(state, playlistId),
    (state: AppState) => state.creativities.all,
    (playlist, creativities) => {
      if (!playlist) {
        return [];
      }

      return [...playlist.items]
        .sort((left, right) => left.position - right.position)
        .map(item => ({
          item,
          creativity: creativities.find(
            creativity => creativity._id === item.creativity
          ) as ICreativity | undefined,
        }));
    }
  );

export const selectPlaylistTargetSummary = (playlistId: string) =>
  createSelector(
    (state: AppState) => selectPlaylistById(state, playlistId),
    playlist => {
      if (!playlist) {
        return '';
      }

      if (playlist.allPlayers) {
        return 'All screens';
      }

      const parts: string[] = [];

      if (playlist.players && playlist.players.length) {
        parts.push(`${playlist.players.length} screens`);
      }

      if (playlist.groups && playlist.groups.length) {
        parts.push(`${playlist.groups.length} groups`);
      }

      return parts.join(', ') || 'No target';
    }
  );
